import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';  // 使用统一的 api 实例

// 获取所有课程数据
export const fetchClasses = createAsyncThunk(
  'techniques/fetchClasses',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching classes...');  // Debug log
      const response = await api.get('/techniques/classes/');
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Handle both array and object responses
      const classes = Array.isArray(response.data) ? response.data : response.data.classes;
      const progress = response.data.progress || {};
      
      if (!classes) {
        throw new Error('No classes data in response');
      }
      
      return {
        classes,
        progress
      };
    } catch (error) {
      console.error('Error fetching classes:', error);  // Debug log
      return rejectWithValue(
        error.response?.data?.error || 
        error.message || 
        'Failed to fetch classes'
      );
    }
  }
);

// 记录课程完成
export const recordClass = createAsyncThunk(
  'techniques/recordClass',
  async ({ classId, index, timestamp = null }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/techniques/classes/${classId}/record_class/`, {
        index: index,
        timestamp: timestamp
      });
      return { classId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to record class');
    }
  }
);

// 删除课程记录
export const removeRecord = createAsyncThunk(
  'techniques/removeRecord',
  async ({ classId, buttonIndex }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/techniques/classes/${classId}/remove_record/`, {
        button_index: buttonIndex
      });
      // Always include classId in the response
      return {
        classId,
        completed_classes: response.data.completed_classes,
        dates: response.data.dates || []
      };
    } catch (error) {
      console.error('Remove record error:', error);
      return rejectWithValue({
        error: error.response?.data || 'Failed to remove record',
        classId // Include classId in error case too
      });
    }
  }
);

// Slice
const techniquesSlice = createSlice({
  name: 'techniques',
  initialState: {
    classes: [],
    userProgress: {},
    loading: false,
    loadingActions: {},  // Add this to track individual loading states
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Add optimistic update reducer
    setClassProgress: (state, action) => {
      const { classId, progress } = action.payload;
      state.userProgress[classId] = progress;
    },
    setLoadingAction: (state, action) => {
      const { classId, isLoading } = action.payload;
      state.loadingActions[classId] = isLoading;
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取课程
      .addCase(fetchClasses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.loading = false;
        state.classes = action.payload.classes;
        state.userProgress = action.payload.progress || {};
        state.error = null;
      })
      .addCase(fetchClasses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
        console.error('Classes fetch failed:', action.payload);  // Debug log
      })
      // 处理记录课程完成
      .addCase(recordClass.pending, (state, action) => {
        const classId = action.meta.arg.classId;
        state.loadingActions[classId] = true;
      })
      .addCase(recordClass.fulfilled, (state, action) => {
        const { classId, completed_classes, dates } = action.payload;
        state.userProgress[classId] = {
          completed_classes,
          dates
        };
        state.loadingActions[classId] = false;  // Clear loading state
      })
      .addCase(recordClass.rejected, (state, action) => {
        const classId = action.meta.arg.classId;
        state.loadingActions[classId] = false;  // Clear loading state on error
      })
      // 处理删除课程记录
      .addCase(removeRecord.pending, (state, action) => {
        const classId = action.meta.arg.classId;
        state.loadingActions[classId] = true;
        state.error = null;
      })
      .addCase(removeRecord.fulfilled, (state, action) => {
        const { classId, completed_classes, dates } = action.payload;
        state.userProgress[classId] = {
          completed_classes,
          dates
        };
        state.loadingActions[classId] = false;
        state.error = null;
      })
      .addCase(removeRecord.rejected, (state, action) => {
        const classId = action.meta.arg.classId;
        state.loadingActions[classId] = false;
        state.error = action.payload?.error || 'Failed to remove record';
      });
  },
});

// 统一导出
export const { clearError, setClassProgress, setLoadingAction } = techniquesSlice.actions;
export default techniquesSlice.reducer;